import React from 'react';
import { Row, Col, Card, Button, Table, Badge } from 'react-bootstrap';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { FaUsers, FaLayerGroup, FaPrint, FaPlus, FaChartBar } from 'react-icons/fa';
import { agentService, groupeService } from '../services/api';

const Accueil = () => {
  const { data: agents = [] } = useQuery('agents', () => 
    agentService.getAll().then(res => res.data)
  );
  
  const { data: groupes = [] } = useQuery('groupes', () => 
    groupeService.getAll().then(res => res.data)
  );

  const { data: statsParFonction = [] } = useQuery('statsParFonction', () => 
    agentService.getStatsParFonction().then(res => res.data)
  );

  const agentsActifs = agents.filter(agent => agent.actif);
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1;
  const currentYear = currentDate.getFullYear();

  return (
    <div>
      <div className="mb-4">
        <h1>Tableau de Bord - Gestion des Cartes de Pointage</h1>
        <p className="text-muted">
          Gérez vos agents, leurs groupes et générez leurs cartes de pointage mensuelles.
        </p>
      </div>

      {/* Statistiques */}
      <Row className="mb-4">
        <Col md={4}>
          <Card className="h-100 border-primary">
            <Card.Body className="text-center">
              <FaUsers size={48} className="text-primary mb-3" />
              <h3 className="text-primary">{agentsActifs.length}</h3>
              <p className="mb-0">Agents Actifs</p>
              <small className="text-muted">sur {agents.length} au total</small>
              
              {/* Statistiques par fonction intégrées */}
              {statsParFonction.length > 0 && (
                <div className="mt-3 pt-3 border-top">
                  <h6 className="text-muted mb-2">Répartition par fonction :</h6>
                  <div className="d-flex flex-column gap-1">
                    {statsParFonction.slice(0, 3).map((stat, index) => {
                      const fonction = stat[0];
                      const total = stat[1];
                      const actifs = stat[2];
                      
                      return (
                        <div key={index} className="d-flex justify-content-between align-items-center small">
                          <span className="text-start" style={{fontSize: '0.8rem'}}>
                            {fonction || 'Non définie'}
                          </span>
                          <div className="d-flex gap-1">
                            <Badge bg="success" className="fs-7">{actifs}</Badge>
                            <Badge bg="secondary" className="fs-7">{total - actifs}</Badge>
                          </div>
                        </div>
                      );
                    })}
                    {statsParFonction.length > 3 && (
                      <small className="text-muted mt-1">
                        +{statsParFonction.length - 3} autres fonctions
                      </small>
                    )}
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="h-100 border-success">
            <Card.Body className="text-center">
              <FaLayerGroup size={48} className="text-success mb-3" />
              <h3 className="text-success">{groupes.length}</h3>
              <p className="mb-0">Groupes</p>
              <small className="text-muted">configurés</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="h-100 border-info">
            <Card.Body className="text-center">
              <FaPrint size={48} className="text-info mb-3" />
              <h3 className="text-info">{currentMonth}/{currentYear}</h3>
              <p className="mb-0">Mois Courant</p>
              <small className="text-muted">pour les cartes</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Actions rapides */}
      <Row>
        <Col md={6}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">
                <FaUsers className="me-2" />
                Gestion des Agents
              </h5>
            </Card.Header>
            <Card.Body>
              <p>Ajoutez, modifiez ou supprimez des agents. Uploadez leurs photos pour les cartes de pointage.</p>
              <div className="d-grid gap-2">
                <Button as={Link} to="/agents" variant="primary">
                  <FaUsers className="me-2" />
                  Gérer les Agents
                </Button>
                <Button as={Link} to="/agents" variant="outline-primary">
                  <FaPlus className="me-2" />
                  Ajouter un Agent
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">
                <FaLayerGroup className="me-2" />
                Gestion des Groupes
              </h5>
            </Card.Header>
            <Card.Body>
              <p>Organisez vos agents en groupes pour faciliter la génération des cartes par équipe.</p>
              <div className="d-grid gap-2">
                <Button as={Link} to="/groupes" variant="success">
                  <FaLayerGroup className="me-2" />
                  Gérer les Groupes
                </Button>
                <Button as={Link} to="/groupes" variant="outline-success">
                  <FaPlus className="me-2" />
                  Créer un Groupe
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Statistiques par fonction */}
      <Row className="mt-4">
        <Col>
          <Card>
            <Card.Header>
              <h5 className="mb-0">
                <FaChartBar className="me-2" />
                Répartition des Agents par Fonction
              </h5>
            </Card.Header>
            <Card.Body>
              {statsParFonction.length > 0 ? (
                <Table responsive striped hover>
                  <thead>
                    <tr>
                      <th>Fonction</th>
                      <th className="text-center">Total Agents</th>
                      <th className="text-center">Agents Actifs</th>
                      <th className="text-center">Agents Inactifs</th>
                      <th className="text-center">Pourcentage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statsParFonction.map((stat, index) => {
                      const fonction = stat[0];
                      const total = stat[1];
                      const actifs = stat[2];
                      const inactifs = total - actifs;
                      const pourcentage = ((total / agents.length) * 100).toFixed(1);
                      
                      return (
                        <tr key={index}>
                          <td>
                            <strong>{fonction || 'Non définie'}</strong>
                          </td>
                          <td className="text-center">
                            <Badge bg="primary" className="fs-6">{total}</Badge>
                          </td>
                          <td className="text-center">
                            <Badge bg="success" className="fs-6">{actifs}</Badge>
                          </td>
                          <td className="text-center">
                            <Badge bg="secondary" className="fs-6">{inactifs}</Badge>
                          </td>
                          <td className="text-center">
                            <Badge bg="info" className="fs-6">{pourcentage}%</Badge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              ) : (
                <div className="text-center text-muted py-4">
                  <FaChartBar size={48} className="mb-3" />
                  <p>Aucune donnée disponible pour le moment</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mt-4">
        <Col>
          <Card className="bg-light">
            <Card.Header>
              <h5 className="mb-0">
                <FaPrint className="me-2" />
                Génération des Cartes de Pointage
              </h5>
            </Card.Header>
            <Card.Body>
              <p>
                Générez et imprimez les cartes de pointage mensuelles avec:
              </p>
              <ul>
                <li>Période du 21 du mois précédent au 20 du mois courant</li>
                <li>Coloration automatique des dimanches et jours fériés</li>
                <li>Photo et informations de l'agent</li>
                <li>Logo de l'entreprise</li>
              </ul>
              <div className="d-grid">
                <Button as={Link} to="/cartes-pointage" variant="info" size="lg">
                  <FaPrint className="me-2" />
                  Générer les Cartes de Pointage
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Accueil; 